import React, { useMemo, useState } from 'react';
import { CalendarDays, ChevronRight, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { ApplicationSummary, Interview } from '../../types';
import { dayBucket, formatDate, formatTime, DayBucket } from '../../utils/formatDate';
import { applicationsService, getApiErrorMessage } from '../../services/api';

interface InterviewEntry {
  application: ApplicationSummary;
  interview: Interview;
}

interface Props {
  interviews: InterviewEntry[];
}

const BUCKET_ORDER: DayBucket[] = ['today', 'tomorrow', 'week', 'later'];

const BUCKET_LABEL: Record<DayBucket, string> = {
  today: 'Today',
  tomorrow: 'Tomorrow',
  week: 'This week',
  later: 'Later',
};

const BUCKET_ACCENT: Record<DayBucket, string> = {
  today: 'bg-emerald-500',
  tomorrow: 'bg-amber-400',
  week: 'bg-indigo-400',
  later: 'bg-zinc-300',
};

const CalendarStrip: React.FC<Props> = ({ interviews }) => {
  const groups = useMemo(() => {
    const map = new Map<DayBucket, InterviewEntry[]>();
    for (const entry of interviews) {
      const bucket = dayBucket(entry.interview.scheduledAt);
      if (!bucket) continue;
      const list = map.get(bucket) ?? [];
      list.push(entry);
      map.set(bucket, list);
    }
    return BUCKET_ORDER.filter((b) => (map.get(b)?.length ?? 0) > 0).map((b) => ({
      bucket: b,
      entries: map.get(b)!,
    }));
  }, [interviews]);

  const [busyId, setBusyId] = useState<number | null>(null);

  const handleDownloadIcs = async (interviewId: number) => {
    setBusyId(interviewId);
    try {
      await applicationsService.downloadIcs(interviewId);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not download the calendar invite.'));
    } finally {
      setBusyId(null);
    }
  };

  if (groups.length === 0) {
    return (
      <div className="bg-white border border-zinc-200 rounded-card px-4 py-3 flex items-center gap-2 text-sm text-zinc-400">
        <CalendarDays className="w-4 h-4 text-zinc-300" />
        No upcoming interviews. Add one from an application's detail view.
      </div>
    );
  }

  return (
    <div className="bg-white border border-zinc-200 rounded-card overflow-hidden">
      {groups.map((group) => (
        <div key={group.bucket} className="px-4 py-3 border-b border-zinc-100 last:border-b-0">
          <div className="flex items-center gap-2 mb-2">
            <span className={`w-1.5 h-1.5 rounded-full ${BUCKET_ACCENT[group.bucket]}`} />
            <span className="text-xs font-semibold text-foreground uppercase tracking-wide">
              {BUCKET_LABEL[group.bucket]}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {group.entries.map(({ application, interview }) => (
              <div
                key={interview.id}
                className="inline-flex items-center gap-2 border border-zinc-200 rounded-btn px-2.5 py-1.5 bg-zinc-50 text-xs"
              >
                <CalendarDays className="w-3.5 h-3.5 text-indigo-500" />
                <span className="font-medium text-foreground">
                  {formatDate(interview.scheduledAt)} · {formatTime(interview.scheduledAt)}
                </span>
                <span className="text-zinc-500 truncate max-w-[220px]">
                  {interview.round} — {application.jobTitle} @ {application.organizationName}
                </span>
                <button
                  onClick={() => handleDownloadIcs(interview.id)}
                  disabled={busyId === interview.id}
                  className="inline-flex items-center gap-1 text-indigo-600 hover:text-brand-700 disabled:opacity-40 transition-colors"
                  title="Download calendar invite (.ics)"
                >
                  <Download className="w-3.5 h-3.5" />
                  <ChevronRight className="w-3 h-3 -ml-1.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default CalendarStrip;